import { useEffect } from 'react';
import { create } from 'zustand';
import { api } from './api';
import type { Media, Overview } from './types';

/** Dashboard data, polled while the tab is visible. One poller is shared by every consumer. */
export const useOverview = create<{ data: Overview | null; at: number; refresh: () => Promise<void> }>((set) => ({
  data: null,
  at: 0,
  refresh: async () => {
    const data = await api.overview().catch(() => null);
    if (data) set({ data, at: Date.now() });
  },
}));

export const useMediaList = create<{ items: Media[]; loaded: boolean; refresh: () => Promise<void> }>((set) => ({
  items: [],
  loaded: false,
  refresh: async () => {
    const items = await api.media().catch(() => null);
    if (items) set({ items, loaded: true });
  },
}));

let subscribers = 0;
let timer: ReturnType<typeof setInterval> | undefined;

function tick(force = false) {
  // Background tabs skip polling; the first load always fetches.
  if (!force && document.visibilityState !== 'visible') return;
  useOverview.getState().refresh();
  useMediaList.getState().refresh();
}

const onVisible = () => tick();

/** Mount once per page that needs live library data. */
export function useLivePolling(intervalMs = 3000) {
  useEffect(() => {
    subscribers++;
    if (subscribers === 1) {
      tick(true);
      timer = setInterval(tick, intervalMs);
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      subscribers--;
      if (subscribers === 0) {
        clearInterval(timer);
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [intervalMs]);
}
